import {
	isNotNilOrEmpty,
	convertArbitraryValueForTrackingId,
	generateTrackingId
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { IconButton, IconButtonSize } from "../IconButton/IconButton";
import { FontIconName } from "../Icon/Icon";
import { ActionListItem } from "../ActionList/ActionListItem";

export type OptionsMenuItem<TValue> = {
	text: string;
	value: TValue;
	valueTrackingId?: string;
};

export type OptionsMenuProps<TValue> = {
	title: string;
	value: TValue;
	onChange(value: TValue): void;
	options: readonly OptionsMenuItem<TValue>[];
	icon?: FontIconName;
	trackingId?: string;
	buttonSize?: IconButtonSize;
	isDisabled?: boolean;
};

export const OptionsMenu = <TValue,>(props: OptionsMenuProps<TValue>): ReactElement => {
	const {
		value,
		onChange,
		options,
		icon = "bars-sort",
		trackingId,
		title,
		buttonSize,
		isDisabled = false
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const changeHandler = (item: OptionsMenuItem<TValue>): void => {
		onChange(item.value);
	};

	const getItemValueTrackingId = useCallback(
		(item: OptionsMenuItem<TValue>): string => {
			if (isNotNilOrEmpty(item.valueTrackingId)) {
				return item.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(item.value);
		},
		[]
	);

	return (
		<>
			<IconButton
				icon={icon}
				kind="secondary"
				onClick={() => setIsOpen(x => !x)}
				trackingId={trackingId}
				ref={ref}
				size={buttonSize}
				isDisabled={isDisabled}
			/>

			{isOpen && ref.current && (
				<DropOverActionList
					target={ref.current}
					contentAlignment="tr"
					targetAlignment="br"
					onClose={() => setIsOpen(false)}
					menuTitle={title}
				>
					{options.map((item, index) => {
						const isSelected: boolean = deepComparison(value, item.value);
						return (
							<ActionListItem
								key={index}
								isActive={isSelected}
								rightIcon={isSelected ? "check" : undefined}
								text={item.text}
								onClick={() => changeHandler(item)}
								trackingId={generateTrackingId(trackingId, `__item|${getItemValueTrackingId(item)}`)}
							/>
						);
					})}
				</DropOverActionList>
			)}
		</>
	);
};
OptionsMenu.displayName = "OptionsMenu";

export function getPropertySortText(propertyKey: LocalizationKey, sortKey: LocalizationKey): string {
	return localize("_PropertySortFormat")
		.replace("{{PropertyName}}", localize(propertyKey))
		.replace("{{SortName}}", localize(sortKey));
}